import { z } from "zod";

/** Billing product identifiers — used in both Zod schema validation and display logic */
export const BILLING_PRODUCTS = ["maac", "caac", "daac"] as const;

/**
 * Billing feature identifiers — used in both Zod schema validation and display logic.
 * Order mirrors the backend interlude definition — do not reorder for cosmetic or
 * semantic grouping; frontend-only reordering risks cross-repo inconsistency.
 */
export const BILLING_FEATURES = [
  "pnp",
  "sms",
  "mms",
  "email",
  "ai_video_content_gen",
  "ai_image_content_gen",
  "ai_segment",
  "ai_journey_insight",
  "ai_product_recommendation",
  "ai_summary",
  "ai_suggest_reply",
  "ai_agent_message",
  "ai_agent_conversation",
  "ai_template_recommendation",
  "voice",
  "studio_question",
] as const;

/** Known top-up status values */
export const TOP_UP_STATUSES = [
  "pending",
  "processing",
  "awaiting_payment",
  "paid",
  "failed",
  "cancelled",
] as const;

/** Credit source types for balance transactions */
export const CREDIT_SOURCE_TYPES = [
  "topup",
  "manual_adjustment",
  "usage_settlement_item",
] as const;

/** Payment methods for top-ups */
export const PAYMENT_METHODS = ["credit_card", "wire_transfer"] as const;

export const BillingProductSchema = z.enum(BILLING_PRODUCTS);
export const BillingFeatureSchema = z.enum(BILLING_FEATURES);
export const TopUpStatusSchema = z.enum(TOP_UP_STATUSES);
export const CreditSourceTypeSchema = z.enum(CREDIT_SOURCE_TYPES);
export const PaymentMethodSchema = z.enum(PAYMENT_METHODS);

export type BillingProduct = z.infer<typeof BillingProductSchema>;
export type BillingFeature = z.infer<typeof BillingFeatureSchema>;
export type TopUpStatus = z.infer<typeof TopUpStatusSchema>;
export type CreditSourceType = z.infer<typeof CreditSourceTypeSchema>;
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

/** Features currently metered — only these have reliable usage data */
export const ACTIVE_BILLING_FEATURES: ReadonlySet<BillingFeature> =
  new Set<BillingFeature>(["sms", "pnp", "mms"]);

/** Compare two features by their defined order in BILLING_FEATURES */
export const compareBillingFeature = (
  a: BillingFeature,
  b: BillingFeature,
): number => BILLING_FEATURES.indexOf(a) - BILLING_FEATURES.indexOf(b);

/** Display names for billing products */
export const PRODUCT_DISPLAY_NAMES: Record<BillingProduct, string> = {
  maac: "MAAC",
  caac: "CAAC",
  daac: "DAAC",
};

/** Display names for billing features */
export const FEATURE_DISPLAY_NAMES: Record<BillingFeature, string> = {
  pnp: "PNP",
  sms: "SMS",
  mms: "MMS",
  email: "Email",
  ai_video_content_gen: "AI Video Content Gen",
  ai_image_content_gen: "AI Image Content Gen",
  ai_segment: "AI Segment",
  ai_journey_insight: "AI Journey Insight",
  ai_product_recommendation: "AI Product Recommendation",
  ai_summary: "AI Summary",
  ai_suggest_reply: "AI Suggest Reply",
  ai_agent_message: "AI Agent Message",
  ai_agent_conversation: "AI Agent Conversation",
  ai_template_recommendation: "AI Template Recommendation",
  voice: "Voice",
  studio_question: "Studio Question",
};
